// src/features/hotels/hooks/useHotelPermissions.ts

import { useEffect, useState, useMemo } from 'react';
import { 
  canList, 
  canCreate, 
  canEdit, 
  canDelete, 
  canView 
} from '../../../utils/permissions';
import { Hotel } from '../types';

export interface UseHotelPermissionsResult {
  // Permisos simplificados para el contexto actual
  canList: boolean;
  canCreate: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canView: boolean;
  
  // Permisos específicos para configuración
  canManageZones: boolean;
  canManageTexts: boolean;
  canManageNotifications: boolean;
  canManageFields: boolean;
  
  // Estado de carga
  loading: boolean;
  
  // Información de contexto
  userRole: string | null;
  currentUserId: string | null;
  currentUserGroupId: string | null;
  currentUserHotelId: string | null;
  
  // Métodos de utilidad
  hasRole: (role: string) => boolean;
  isAtLeastRole: (minRole: string) => boolean;
  canAccessHotel: (hotel: Hotel) => boolean;
}

/**
 * Hook para gestionar permisos de hoteles
 * Versión simplificada y consistente
 */
export const useHotelPermissions = (hotel?: Hotel | null): UseHotelPermissionsResult => {
  const [loading, setLoading] = useState(true);
  
  // Obtener datos del usuario del localStorage
  const currentRole = localStorage.getItem('role');
  const currentUserId = localStorage.getItem('userId');
  const currentUserGroupId = localStorage.getItem('groupId');
  const currentUserHotelId = localStorage.getItem('hotelId');

  useEffect(() => {
    // Simular carga mínima para consistencia
    const timer = setTimeout(() => setLoading(false), 100);
    return () => clearTimeout(timer);
  }, []);

  const permissions = useMemo(() => {
    if (!currentRole) {
      return {
        canList: false,
        canCreate: false,
        canEdit: false,
        canDelete: false,
        canView: false,
        canManageZones: false,
        canManageTexts: false,
        canManageNotifications: false,
        canManageFields: false,
        loading,
        userRole: null,
        currentUserId: null,
        currentUserGroupId: null,
        currentUserHotelId: null,
        hasRole: () => false,
        isAtLeastRole: () => false,
        canAccessHotel: () => false,
      };
    }

    // 🎯 FUNCIONES DE UTILIDAD
    const hasRole = (role: string): boolean => {
      return currentRole === role;
    };

    const roleHierarchy = ['SuperAdmin', 'GroupAdmin', 'HotelAdmin', 'Receptionist'];
    const isAtLeastRole = (minRole: string): boolean => {
      const currentIndex = roleHierarchy.indexOf(currentRole);
      const minIndex = roleHierarchy.indexOf(minRole);
      return currentIndex !== -1 && minIndex !== -1 && currentIndex <= minIndex;
    };

    /**
     * Verifica si el usuario puede acceder a un hotel específico
     */
    const canAccessHotel = (hotelToCheck: Hotel): boolean => {
      // SuperAdmin puede acceder a cualquier hotel
      if (currentRole === 'SuperAdmin') return true;
      
      // GroupAdmin puede acceder a hoteles de su grupo
      if (currentRole === 'GroupAdmin') {
        return hotelToCheck.groupId === currentUserGroupId;
      }
      
      // HotelAdmin puede acceder solo a su hotel
      if (currentRole === 'HotelAdmin') {
        return hotelToCheck.id === currentUserHotelId;
      }
      
      // Receptionist puede acceder solo a su hotel (solo lectura)
      if (currentRole === 'Receptionist') {
        return hotelToCheck.id === currentUserHotelId;
      }
      
      return false;
    };

    // Permisos básicos del recurso usando las funciones existentes
    const baseCanList = canList(currentRole, 'hotels');
    const baseCanCreate = canCreate(currentRole, 'hotels');
    const baseCanEdit = canEdit(currentRole, 'hotels');
    const baseCanDelete = canDelete(currentRole, 'hotels');
    const baseCanView = canView(currentRole, 'hotels') || baseCanList;

    // Si se proporciona un hotel específico, aplicar lógica contextual
    let finalCanEdit = baseCanEdit;
    let finalCanDelete = baseCanDelete;

    if (hotel) {
      // Verificar acceso al hotel específico
      const hasHotelAccess = canAccessHotel(hotel);
      
      if (!hasHotelAccess) {
        finalCanEdit = false;
        finalCanDelete = false;
      } else {
        // Aplicar restricciones por rol
        switch (currentRole) {
          case 'SuperAdmin':
            // SuperAdmin mantiene todos los permisos
            break;
            
          case 'GroupAdmin':
            // GroupAdmin puede editar hoteles de su grupo, pero no eliminar
            finalCanDelete = false;
            break;
            
          case 'HotelAdmin':
            // HotelAdmin puede editar su hotel, pero no eliminar
            finalCanDelete = false;
            break;
            
          case 'Receptionist':
            // Receptionist solo lectura
            finalCanEdit = false;
            finalCanDelete = false;
            break;
            
          default:
            finalCanEdit = false;
            finalCanDelete = false;
        }
      }
    }

    // Los permisos de configuración dependen del permiso de edición
    const canManageConfig = finalCanEdit;

    return {
      // Permisos contextuales (considerando el hotel específico si existe)
      canList: baseCanList,
      canCreate: baseCanCreate,
      canEdit: finalCanEdit,
      canDelete: finalCanDelete,
      canView: baseCanView,
      
      // Permisos de configuración
      canManageZones: canManageConfig,
      canManageTexts: canManageConfig,
      canManageNotifications: canManageConfig,
      canManageFields: canManageConfig,
      
      loading,
      
      // Información de contexto
      userRole: currentRole,
      currentUserId,
      currentUserGroupId,
      currentUserHotelId,
      
      // Métodos de utilidad
      hasRole,
      isAtLeastRole,
      canAccessHotel,
    };
  }, [
    currentRole,
    currentUserId,
    currentUserGroupId,
    currentUserHotelId,
    hotel,
    loading
  ]);

  return permissions;
};

/**
 * Hook simplificado para permisos de lista de hoteles (sin hotel específico)
 */
export const useHotelsListPermissions = () => {
  const basePermissions = useHotelPermissions();

  return {
    // Permisos básicos
    canList: basePermissions.canList,
    canCreate: basePermissions.canCreate,
    canEdit: basePermissions.canEdit,
    canDelete: basePermissions.canDelete,
    canView: basePermissions.canView,
    
    // Permisos adicionales para la lista
    canExport: basePermissions.canList,
    canFilterByGroup: basePermissions.canList,
    
    // Estado
    loading: basePermissions.loading,
    
    // Información de contexto
    userRole: basePermissions.userRole,
    
    // Métodos de utilidad
    hasRole: basePermissions.hasRole,
    isAtLeastRole: basePermissions.isAtLeastRole,
  };
};